import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button, Column, FormattedDurationText, InputJob, Row, Text } from '~/commonComponents';
import { Field, Value } from '~/commonComponents/InputJob/Field';
import BriefcaseIcon from '~/images/briefcase.svg';
import { Colors } from '~/themes/colors';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { OnsiteInterviewFormRules, TBreakTime, TOnsiteInterviewFormData } from './FormRule';
import { useFocusField } from './useFieldFocus';

export enum OnsiteInterviewFormFields {
  AssistedHygiene = 'ASSISTED_HYGIENE',
  Dates = 'DATES',
  HourlyRate = 'HOURLY_RATE',
  Hours = 'HOURS',
  MealBreak = 'MEAL_BREAK',
  Specialty = 'SPECIALTY',
}

interface IOnsiteInterviewFormProps {
  jobTitle: string;
  office: string;
  profession: string;
  btnSecondaryText?: string;
  defaultValues?: TOnsiteInterviewFormData;
  isEdit?: boolean;
  readonlyFields?: OnsiteInterviewFormFields[];
  unavailableDates?: Date[];
  btnSecondaryClick?: () => void;
  onSubmit(formData: TOnsiteInterviewFormData): void;
}

export const OnsiteInterviewForm = ({
  btnSecondaryText,
  defaultValues,
  isEdit = false,
  jobTitle,
  office,
  profession,
  readonlyFields = [],
  unavailableDates = [],
  btnSecondaryClick,
  onSubmit,
}: IOnsiteInterviewFormProps) => {
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
  } = useForm<TOnsiteInterviewFormData>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(OnsiteInterviewFormRules),
  });

  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();

  const { focus, handleFieldFocus } = useFocusField<OnsiteInterviewFormFields>();

  const readonly = new Set(readonlyFields);
  const watchBreakTime = useWatch({ control, name: 'breakTime' });
  const watchHoursArrival = useWatch({ control, name: 'hoursArrival' });
  const watchHoursDeparture = useWatch({ control, name: 'hoursDeparture' });
  const watchIsBreakPaid = useWatch({ control, name: 'isPaidBreakTime' });

  const handleSetHours = useCallback(
    (arrival: string, departure: string) => {
      setValue('hoursArrival', arrival, { shouldValidate: true, shouldDirty: true });
      setValue('hoursDeparture', departure, { shouldValidate: true, shouldDirty: true });
      handleFieldFocus(OnsiteInterviewFormFields.MealBreak);
    },
    [handleFieldFocus, setValue],
  );

  return (
    <Column gap={6} marginInline="auto" width="100%">
      <InputJob.SelectOffice isDisabled value={office} />

      <Field
        IconSrc={BriefcaseIcon}
        isDisabled
        title="Job Title:"
        ValueComponent={() => <Value style={{ maxWidth: 'auto' }} text={jobTitle} />}
      />

      <Controller
        control={control}
        name="dates"
        render={({ field }) => (
          <InputJob.Date
            {...field}
            excluded={unavailableDates}
            isDisabled={readonly.has(OnsiteInterviewFormFields.Dates)}
            isOpen={focus === OnsiteInterviewFormFields.Dates}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.Dates)}
          />
        )}
      />

      <InputJob.Professional isDisabled value={profession} />

      <Controller
        control={control}
        name="specialty"
        render={({ field }) => (
          <InputJob.Specialty
            {...field}
            isDisabled={readonly.has(OnsiteInterviewFormFields.Specialty)}
            isOpen={focus === OnsiteInterviewFormFields.Specialty}
            professional={profession}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.Specialty)}
          />
        )}
      />

      <Controller
        control={control}
        name="isAssistedHygiene"
        render={({ field }) => (
          <InputJob.AssistedHygiene
            {...field}
            isDisabled={readonly.has(OnsiteInterviewFormFields.AssistedHygiene)}
            isOpen={focus === OnsiteInterviewFormFields.AssistedHygiene}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.AssistedHygiene)}
          />
        )}
      />

      <InputJob.Hours
        guidanceText={
          <Text color={Colors.neutral_500} italic>
            Onsite interviews can run up to <FormattedDurationText duration={timeLimitInMinutes} />,{' '}
            giving you the time you need to
            <br />
            evaluate skills and overall fit.
          </Text>
        }
        isDisabled={readonly.has(OnsiteInterviewFormFields.Hours)}
        isOpen={focus === OnsiteInterviewFormFields.Hours}
        timeLimitInMinutes={timeLimitInMinutes}
        valueArrival={watchHoursArrival}
        valueDeparture={watchHoursDeparture}
        onChange={handleSetHours}
        onToggle={handleFieldFocus(OnsiteInterviewFormFields.Hours)}
      />

      <InputJob.MealBreak
        isDisabled={readonly.has(OnsiteInterviewFormFields.MealBreak)}
        isOpen={focus === OnsiteInterviewFormFields.MealBreak}
        valueBreak={watchBreakTime || undefined}
        valueIsPaid={watchIsBreakPaid}
        onToggle={handleFieldFocus(OnsiteInterviewFormFields.MealBreak)}
        onChange={(key, value) => {
          if (key === 'break')
            setValue('breakTime', value as TBreakTime, {
              shouldValidate: true,
              shouldDirty: true,
            });
          if (key === 'is_paid')
            setValue('isPaidBreakTime', value as boolean, {
              shouldValidate: true,
              shouldDirty: true,
            });
        }}
      />

      <Controller
        control={control}
        name="hourlyRate"
        render={({ field }) => (
          <InputJob.HourlyRate
            {...field}
            professional={profession}
            isOpen={focus === OnsiteInterviewFormFields.HourlyRate}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.HourlyRate)}
          />
        )}
      />

      <Row alignItems="center" justifyContent="center" gap={16} marginTop={32}>
        {btnSecondaryClick && btnSecondaryText && (
          <Button
            text={btnSecondaryText}
            style={{ width: 296, backgroundColor: Colors.secondary_500 }}
            onClick={btnSecondaryClick}
          />
        )}
        <Button
          disabled={isEdit ? !isDirty || !isValid : !isValid}
          text={isEdit ? 'Save Changes' : 'Next'}
          style={{ width: 296 }}
          onClick={handleSubmit(onSubmit)}
        />
      </Row>
    </Column>
  );
};
